import React, { useMemo } from "react";
import { Form, Formik } from "formik";
import { useOnSubmitFormik } from "api/hooks/useOnSubmitFormik";
import { Collections } from "api/model/Collections";
import { BoxCard } from "ui/componentes/BoxCard";
import * as Yup from "yup";
import { Car, CarFormDefault } from "api/model/Car";
import { Box, Button, Grid } from "@material-ui/core";
import { SubmitButton } from "ui/componentes/SubmitButton";
import { useCollection } from "api/hooks/useCollection";
import { Brand } from "api/model/Brand";
import {
    SelectFieldFormik,
    SelectOption,
} from "ui/componentes/Form/SelectFieldFormik";
import { InputFieldFormik } from "ui/componentes/Form/InputFieldFormik";
import {
    yupMaxLengthField,
    yupRequiredField,
} from "ui/componentes/Form/functions";
import { useThemeBreakpoints } from "api/hooks/useThemeBreakpoints";
import { FileVisualizer } from "ui/componentes";
import { BucketFolders } from "api";

interface CarFormProps {
    car?: Car;
    onCancel: () => void;
}

export const CarFormValidation = Yup.object().shape({
    brandId: Yup.string().required(yupRequiredField),
    name: Yup.string().required(yupRequiredField).max(50, yupMaxLengthField),
    model: Yup.string().required(yupRequiredField).max(50, yupMaxLengthField),
    description: Yup.string().max(200, yupMaxLengthField),
});

const CarForm: React.FC<CarFormProps> = ({ onCancel, car }) => {
    const onSubmit = useOnSubmitFormik<Car>(Collections.CARS);
    const brands =
        useCollection<Brand[]>({ collection: Collections.BRANDS }, true) || [];
    const brandsOptions = useMemo(
        () =>
            brands.map<SelectOption>(({ name, id }) => ({
                label: name,
                value: id,
            })),
        [brands]
    );

    const { isDownSm } = useThemeBreakpoints();

    return (
        <Formik
            enableReinitialize={true}
            initialValues={{ ...CarFormDefault, ...car }}
            onSubmit={onSubmit}
            validationSchema={CarFormValidation}
        >
            {({ values, submitForm }) => {
                return (
                    <Form>
                        <BoxCard display="flex">
                            <Grid container spacing={1}>
                                <Grid item sm={3} xs={12}>
                                    <SelectFieldFormik
                                        name="brandId"
                                        label="Marca"
                                        options={brandsOptions}
                                    />
                                </Grid>
                                <Grid item sm={3} xs={12}>
                                    <InputFieldFormik
                                        id="name"
                                        name="name"
                                        label="Nome"
                                    />
                                </Grid>
                                <Grid item sm={3} xs={12}>
                                    <InputFieldFormik
                                        id="model"
                                        name="model"
                                        label="Modelo"
                                    />
                                </Grid>
                                <Grid item sm={3} xs={12}>
                                    <InputFieldFormik
                                        id="carIRacingID"
                                        name="carIRacingID"
                                        label="ID no iRacing"
                                        type="number"
                                    />
                                </Grid>
                                <Grid item sm={3} xs={12}>
                                    <InputFieldFormik
                                        id="classIRacingID"
                                        name="classIRacingID"
                                        label="Nome da classe"
                                    />
                                </Grid>
                                <Grid item hidden={isDownSm} xs={6} />
                                <Grid item xs={12}>
                                    <InputFieldFormik
                                        id="description"
                                        name="description"
                                        label="Descrição"
                                        multiline
                                        rows={4}
                                        helperText={`Max.: ${values.description?.length} / 200`}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FileVisualizer
                                        label="Imagem"
                                        file={values.image}
                                        uploadProps={{
                                            name: "image",
                                            accept: "image/*",
                                            folder: BucketFolders.CHAMPIONSHIPS,
                                            disabled: !values.id,
                                            onComplete: () => submitForm(),
                                        }}
                                        onDelete={() => submitForm()}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box
                                        display="flex"
                                        flexDirection="row-reverse"
                                        justifyContent=""
                                    >
                                        <SubmitButton
                                            style={{
                                                marginLeft: 5,
                                            }}
                                            fullWidth={false}
                                        />
                                        <Button
                                            fullWidth={false}
                                            onClick={onCancel}
                                        >
                                            Cancelar
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </BoxCard>
                    </Form>
                );
            }}
        </Formik>
    );
};

export { CarForm };
